import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import { filterApps, parseAppList } from "../api/apps";
import { accumulateWheel, cardinalSwipe, classifyGesture, normalize, wheelToSwipe } from "../api/gestures";
import { cornerFraction, screenAspect, type Size } from "../api/screen";
import { Badge, ErrorBox, Loading } from "../components/common";
import { useI18n } from "../i18n";
import { useDevicePanel, type CaptureState, type PanelStats } from "../hooks/useDevicePanel";
import DeviceModules from "./DeviceModules";

const GLYPHS: Record<string, React.ReactNode> = {
  back: <path d="M15 5l-7 6 7 6" />,
  home: (
    <>
      <path d="M4 11l7-6.5L18 11" />
      <path d="M6.5 10v7h9v-7" />
    </>
  ),
  recents: (
    <>
      <rect x="4" y="5" width="5.5" height="12" rx="1.5" />
      <rect x="11.5" y="5" width="5.5" height="12" rx="1.5" />
    </>
  ),
  enter: <path d="M5 6v6h12m-4-4 4 4-4 4" />,
  notifications: (
    <>
      <path d="M11 4.5a4.5 4.5 0 0 1 4.5 4.5c0 3.5 1.5 4.5 1.5 4.5H5s1.5-1 1.5-4.5A4.5 4.5 0 0 1 11 4.5Z" />
      <path d="M9 16.5a2 2 0 0 0 4 0" />
    </>
  ),
  quick_settings: (
    <>
      <circle cx="11" cy="11" r="3" />
      <path d="M11 4.5v2M11 15.5v2M4.5 11h2M15.5 11h2" />
      <path d="M6.5 6.5l1.4 1.4M14.9 14.9l1.4 1.4M15.5 6.5l-1.4 1.4M7.9 14.9l-1.4 1.4" />
    </>
  ),
  volume_up: (
    <>
      <path d="M4 8.5v5h3l4 3v-11l-4 3H4Z" />
      <path d="M14 11h4M16 9v4" />
    </>
  ),
  volume_down: (
    <>
      <path d="M4 8.5v5h3l4 3v-11l-4 3H4Z" />
      <path d="M14 11h4" />
    </>
  ),
  swipe_up: (
    <>
      <path d="M11 17V6" />
      <path d="M7 10l4-4 4 4" />
    </>
  ),
  swipe_down: (
    <>
      <path d="M11 5v11" />
      <path d="M7 12l4 4 4-4" />
    </>
  ),
  swipe_left: (
    <>
      <path d="M17 11H6" />
      <path d="M10 7l-4 4 4 4" />
    </>
  ),
  swipe_right: (
    <>
      <path d="M5 11h11" />
      <path d="M12 7l4 4-4 4" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="12" height="8" rx="2.5" />
      <path d="M8 10V7.5a3 3 0 0 1 6 0V10" />
    </>
  ),
  unlock: (
    <>
      <rect x="5" y="10" width="12" height="8" rx="2.5" />
      <path d="M8 10V7.5A3 3 0 0 1 13.6 6" />
    </>
  ),
  pencil: <path d="M4 18h3.5L17 8.5a1.8 1.8 0 0 0-2.5-2.5L5 15.5V18Z" />,
  collapse: <path d="M8 5l6 6-6 6" />,
  expand: <path d="M14 5l-6 6 6 6" />,
};

function Icon({ name }: { name: keyof typeof GLYPHS | string }) {
  return (
    <svg
      viewBox="0 0 22 22"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPHS[name]}
    </svg>
  );
}

const KEYS: Array<[labelKey: string, key: string]> = [
  ["panel.back", "back"],
  ["panel.home", "home"],
  ["panel.recents", "recents"],
  ["panel.enter", "enter"],
  ["panel.notifs", "notifications"],
  ["panel.quick", "quick_settings"],
  ["panel.volUp", "volume_up"],
  ["panel.volDown", "volume_down"],
];

const SWIPES: Array<[labelKey: string, dir: "left" | "right" | "up" | "down"]> = [
  ["panel.swipeLeft", "left"],
  ["panel.swipeRight", "right"],
  ["panel.swipeUp", "up"],
  ["panel.swipeDown", "down"],
];

type Tab = "trigger" | "apps" | "modules" | "note";

function formatMs(ms: number): string {
  return ms < 1_000 ? `${Math.round(ms)} ms` : `${(ms / 1_000).toFixed(1)} s`;
}

/** `2.9 fps · 48 ms srv · 210 ms tap · 0.4 s lag` — only what has been measured. */
function formatHud(stats: PanelStats): string {
  const parts = [`${stats.fps.toFixed(1)} fps`];
  if (stats.serverRttMs !== null) parts.push(`${formatMs(stats.serverRttMs)} srv`);
  if (stats.commandRttMs !== null) parts.push(`${formatMs(stats.commandRttMs)} tap`);
  if (stats.frameLagMs !== null) parts.push(`${formatMs(stats.frameLagMs)} lag`);
  return parts.join(" · ");
}

/**
 * What the screen shows instead of a picture. "Waiting for the first frame"
 * is only honest for a few seconds; after that, or as soon as the phone has
 * said why, the reason and the way out replace it.
 */
function StreamNotice({
  connected,
  capture,
  late,
  offline,
  osVersion,
  onRequestCapture,
  onWake,
}: {
  connected: boolean;
  capture: CaptureState | null;
  late: boolean;
  offline: boolean;
  osVersion: string | null;
  onRequestCapture: (prefer: boolean) => void;
  onWake: () => void;
}) {
  const { t } = useI18n();
  if (!connected) {
    // Why there is no picture, said where the picture would have been: this
    // sits inside the fixed-aspect screen, so the message mounting cannot
    // shove the rest of the panel around (a page-level alert did exactly
    // that, on every reconnect attempt).
    return offline ? (
      <div className="notice">
        <p>{t("panel.deviceOffline")}</p>
        <p className="muted">{t("panel.reconnecting")}</p>
      </div>
    ) : (
      <>{t("panel.noStream")}</>
    );
  }
  if (capture && !capture.available) {
    switch (capture.reason) {
      case "needs_projection_consent":
      case "projection_stopped":
        return (
          <div className="notice">
            <p>
              {capture.reason === "projection_stopped"
                ? t("panel.captureStopped")
                : t("panel.captureNeedsConsent", { version: osVersion ?? "" })}
            </p>
            <button className="primary" onClick={() => onRequestCapture(false)}>
              {t("panel.requestCapture")}
            </button>
            <p className="muted">{t("panel.captureConsentHint")}</p>
          </div>
        );
      case "needs_agent_update":
        return (
          <div className="notice">
            <p>{t("panel.captureNeedsAgentUpdate", { version: osVersion ?? "" })}</p>
          </div>
        );
      case "accessibility_off":
        return (
          <div className="notice">
            <p>{t("panel.captureAccessibilityOff")}</p>
          </div>
        );
      case "screen_off":
        return (
          <div className="notice">
            <p>{t("panel.captureScreenOff")}</p>
            <button onClick={onWake}>{t("panel.wake")}</button>
          </div>
        );
      case "platform_error":
        return (
          <div className="notice">
            <p>{t("panel.capturePlatformError", { message: capture.message })}</p>
            <button onClick={() => onRequestCapture(true)}>
              {t("panel.requestCaptureFallback")}
            </button>
          </div>
        );
      default:
        return <>{capture.message || t("panel.waitingFrame")}</>;
    }
  }
  if (late) {
    return (
      <div className="notice">
        <p>{t("panel.noFrameYet")}</p>
      </div>
    );
  }
  return <>{t("panel.waitingFrame")}</>;
}

export default function DevicePanel() {
  const { id = "" } = useParams();
  const { t } = useI18n();
  const device = useQuery({ queryKey: ["device", id], queryFn: () => api.device(id) });
  const {
    frame,
    cornerRadius,
    connected,
    reconnecting,
    deviceOffline,
    stalled,
    error,
    stats,
    capture,
    firstFrameLate,
    send,
  } = useDevicePanel(id);

  const [typed, setTyped] = useState("");
  const [lastError, setLastError] = useState<string | null>(null);

  // The box on screen takes the shape of the phone: each frame's own size
  // (so a rotation turns it) and the corner rounding the phone reports.
  const [frameSize, setFrameSize] = useState<Size | null>(null);
  useEffect(() => setFrameSize(null), [id]);
  function onFrameLoad(event: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
    if (!width || !height) return;
    setFrameSize((size) =>
      size && size.width === width && size.height === height ? size : { width, height },
    );
  }

  // One side rail with tabs instead of a stack of cards: the phone keeps the
  // width, and the rail folds away entirely when it is not needed.
  const [tab, setTab] = useState<Tab>("trigger");
  const [sideOpen, setSideOpen] = useState(true);
  const [appFilter, setAppFilter] = useState("");
  const [launching, setLaunching] = useState<string | null>(null);

  // Device identity + note editing (PATCH /devices/{id}).
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [noteDraft, setNoteDraft] = useState<string | null>(null);

  // Trigger arming: which scenario this phone watches for.
  const triggers = useQuery({ queryKey: ["triggers"], queryFn: api.triggers });
  const [triggerChoice, setTriggerChoice] = useState<string | null>(null);
  // The phone's own captures: frames + the videos stitched from them.
  const captures = useQuery({
    queryKey: ["captures", id],
    queryFn: () => api.triggerCaptures(id),
    refetchInterval: 15_000,
  });
  const latestVideo = (captures.data ?? []).find((c) => c.kind === "timelapse");
  const videoUrl = useQuery({
    queryKey: ["capture-video", latestVideo?.id],
    queryFn: () => api.artifactUrl(latestVideo!.id),
    enabled: !!latestVideo,
    staleTime: 5 * 60 * 1000,
  });

  const screenRef = useRef<HTMLDivElement>(null);
  const pressStart = useRef<{ x: number; y: number; at: number } | null>(null);
  const wheelAccum = useRef<{ x: number; deltaY: number } | null>(null);
  const wheelRaf = useRef<number | null>(null);

  const point = (event: { clientX: number; clientY: number }) =>
    normalize(event.clientX, event.clientY, screenRef.current!.getBoundingClientRect());

  async function run(cmd: string, args: Record<string, unknown> = {}) {
    try {
      setLastError(null);
      return await send(cmd, args);
    } catch (exc) {
      setLastError(exc instanceof Error ? exc.message : String(exc));
      return null;
    }
  }

  /** Gestures must not wait for the phone's ack — awaiting made every tap
   *  feel like it lagged a round trip behind the click. */
  function fire(cmd: string, args: Record<string, unknown> = {}) {
    void run(cmd, args);
  }

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    const { x, y } = point(event);
    pressStart.current = { x, y, at: Date.now() };
  }

  function onPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const start = pressStart.current;
    pressStart.current = null;
    // The second click of a double-click is a double_tap, not another tap.
    if (!start || event.detail > 1) return;

    const gesture = classifyGesture(start, point(event), Date.now() - start.at);
    const { kind, ...args } = gesture;
    fire(kind, args);
  }

  function onPointerCancel(event: React.PointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pressStart.current = null;
  }

  function onDoubleClick(event: React.MouseEvent) {
    const { x, y } = point(event);
    fire("double_tap", { x, y });
  }

  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;

    const flushWheel = () => {
      wheelRaf.current = null;
      const acc = wheelAccum.current;
      wheelAccum.current = null;
      if (!acc || Math.abs(acc.deltaY) < 1) return;
      fire("swipe", wheelToSwipe(acc.x, acc.deltaY));
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      wheelAccum.current = accumulateWheel(wheelAccum.current, point(event).x, event.deltaY);
      if (wheelRaf.current != null) return;
      wheelRaf.current = requestAnimationFrame(flushWheel);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (wheelRaf.current != null) cancelAnimationFrame(wheelRaf.current);
    };
    // Re-bind once the screen node exists (it is not mounted while the device
    // query is still loading).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, send, device.data]);

  function sendKey(key: string) {
    fire("key", { key });
  }

  function sendSwipe(direction: "left" | "right" | "up" | "down") {
    fire("swipe", cardinalSwipe(direction));
  }

  async function launchApp(pkg: string) {
    setLaunching(pkg);
    try {
      await run("launch_app", { package: pkg });
    } finally {
      setLaunching(null);
    }
  }

  async function sendText() {
    if (!typed) return;
    await run("type", { text: typed });
    setTyped("");
  }

  const appsQuery = useQuery({
    queryKey: ["device-apps", id],
    queryFn: async () => parseAppList(await send("list_apps")),
    enabled: connected && tab === "apps",
    staleTime: 30_000,
  });
  const visibleApps = filterApps(appsQuery.data ?? [], appFilter);

  const rename = useMutation({
    mutationFn: () => api.updateDevice(id, { name: deviceName }),
    onSuccess: () => {
      setRenaming(false);
      queryClient.invalidateQueries({ queryKey: ["device", id] });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });

  const saveNote = useMutation({
    mutationFn: () => api.updateDevice(id, { note: noteDraft }),
    onSuccess: () => {
      setNoteDraft(null);
      queryClient.invalidateQueries({ queryKey: ["device", id] });
    },
  });

  const armTrigger = useMutation({
    mutationFn: (scenarioId: string | null) =>
      api.updateDevice(id, { trigger_scenario_id: scenarioId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device", id] });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });

  async function devicePower(cmd: "lock_screen" | "unlock_screen") {
    try {
      setLastError(null);
      const result = await api.deviceAction(id, cmd);
      if (!result.ok) setLastError(result.error ?? t("panel.deviceRefused"));
    } catch (exc) {
      setLastError(exc instanceof Error ? exc.message : String(exc));
    }
  }

  if (device.isLoading) return <Loading what={t("loading.device")} />;
  if (device.error) return <ErrorBox error={device.error} />;

  const meta = [
    device.data!.model,
    device.data!.os_version ? `${device.data!.platform} ${device.data!.os_version}` : null,
    device.data!.battery_level !== null ? `${device.data!.battery_level}%` : null,
  ].filter(Boolean) as string[];

  const armedTrigger = device.data!.trigger_name;
  const hasModules = Object.keys(device.data!.modules ?? {}).length > 0;
  const phoneShape = {
    "--aspect": screenAspect(frameSize, device.data),
    "--corner": cornerFraction(cornerRadius),
  } as React.CSSProperties;
  const armedConfig = (triggers.data ?? []).find(
    (t) => t.id === device.data!.trigger_scenario_id,
  )?.config;

  function conditionSummary(config: NonNullable<typeof armedConfig>): string {
    return config.type === "app_launch"
      ? t("panel.condWhenPackage", { package: config.package ?? "" })
      : t("panel.condWhenText", { text: config.text ?? "" });
  }

  return (
    <div className="device-page">
      <div className="panel-topbar">
        {renaming ? (
          <div className="title" style={{ flex: 1 }}>
            <input
              value={deviceName}
              autoFocus
              style={{ maxWidth: 280 }}
              onChange={(e) => setDeviceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && deviceName.trim()) rename.mutate();
                if (e.key === "Escape") setRenaming(false);
              }}
            />
            <button
              className="primary"
              disabled={!deviceName.trim() || rename.isPending}
              onClick={() => rename.mutate()}
            >
              {t("panel.save")}
            </button>
            <button onClick={() => setRenaming(false)}>{t("panel.cancel")}</button>
          </div>
        ) : (
          <>
            <div className="title">
              <h2>{device.data!.name}</h2>
              <Badge value={device.data!.status} />
              <button
                className="icon-btn"
                title={t("panel.rename")}
                onClick={() => {
                  setDeviceName(device.data!.name);
                  setRenaming(true);
                }}
              >
                <Icon name="pencil" />
              </button>
            </div>
            <div className="meta">
              {meta.map((item) => (
                <span key={item} className="chip">
                  {item}
                </span>
              ))}
              {device.data!.foreground_app && (
                <span className="chip mono" title={t("panel.foreground")}>
                  {device.data!.foreground_app}
                </span>
              )}
            </div>
          </>
        )}

        <div className="spacer" />

        <span className={`live ${connected ? (stalled ? "stalled" : "on") : ""}`}>
          <i className="dot" />
          {connected
            ? stalled
              ? t("panel.stalled")
              : t("panel.streaming")
            : reconnecting
              ? t("panel.reconnecting")
              : t("panel.notConnected")}
        </span>
        {connected && (
          <span
            className="hud mono"
            title={t("panel.hudTitle")}
          >
            {formatHud(stats)}
          </span>
        )}

        <div className="toolgroup">
          <button onClick={() => devicePower("lock_screen")} title={t("panel.lockTitle")}>
            <Icon name="lock" />
          </button>
          <button
            onClick={() => devicePower("unlock_screen")}
            title={t("panel.unlockTitle")}
          >
            <Icon name="unlock" />
          </button>
        </div>

        <button
          className="icon-btn"
          onClick={() => setSideOpen((open) => !open)}
          title={sideOpen ? t("panel.hidePanel") : t("panel.showPanel")}
        >
          <Icon name={sideOpen ? "collapse" : "expand"} />
        </button>
      </div>

      {rename.error && (
        <div className="alert">
          <ErrorBox error={rename.error} />
        </div>
      )}
      {armTrigger.error && (
        <div className="alert">
          <ErrorBox error={armTrigger.error} />
        </div>
      )}
      {error && (
        <div className="alert">
          <ErrorBox error={new Error(error)} />
        </div>
      )}
      {lastError && (
        <div className="alert">
          <ErrorBox error={new Error(lastError)} />
        </div>
      )}

      <div className="panel">
        <section className="stage">
          <div className="stage-body">
            {/* The wrap is what the phone is measured against (see the CSS):
                the picture and the key rail sit centred inside it as a pair. */}
            <div className="screen-wrap">
              <div className="phone" style={phoneShape}>
                <div
                  className="screen"
                  ref={screenRef}
                  onPointerDown={onPointerDown}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerCancel}
                  onDoubleClick={onDoubleClick}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {frame ? (
                    <img
                      src={frame}
                      alt={t("panel.screenAlt")}
                      draggable={false}
                      onLoad={onFrameLoad}
                    />
                  ) : (
                    <div className="placeholder">
                      <StreamNotice
                        connected={connected}
                        capture={capture}
                        late={firstFrameLate}
                        offline={deviceOffline}
                        osVersion={device.data!.os_version}
                        onRequestCapture={(prefer) => fire("start_projection", { prefer })}
                        onWake={() => devicePower("unlock_screen")}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="keypad">
                {KEYS.map(([labelKey, key]) => (
                  <button key={key} onClick={() => sendKey(key)} title={t(labelKey)}>
                    <Icon name={key} />
                    {t(labelKey)}
                  </button>
                ))}
                <div className="keypad-sep" />
                {SWIPES.map(([labelKey, dir]) => (
                  <button
                    key={dir}
                    onClick={() => sendSwipe(dir)}
                    title={t("panel.swipeTitle", { dir: t(labelKey).toLowerCase() })}
                  >
                    <Icon name={`swipe_${dir}`} />
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="stage-foot">
            <input
              placeholder={t("panel.typePlaceholder")}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendText()}
            />
            <button className="primary" onClick={sendText} disabled={!typed}>
              {t("panel.send")}
            </button>
            <span className="hint">{t("panel.gestureHint")}</span>
          </div>
        </section>

        {sideOpen && (
          <aside className="side">
            <div className="tabs">
              <button className={tab === "trigger" ? "on" : ""} onClick={() => setTab("trigger")}>
                {t("panel.tabTrigger")}
              </button>
              <button className={tab === "apps" ? "on" : ""} onClick={() => setTab("apps")}>
                {t("panel.tabApps")}
              </button>
              {/* Only for a phone whose APK actually carries one: the tab is
                  the answer to "what did this build get", and an empty one on
                  every other phone is noise (MODULES §4.4). */}
              {hasModules && (
                <button
                  className={tab === "modules" ? "on" : ""}
                  onClick={() => setTab("modules")}
                >
                  {t("panel.tabModules")}
                </button>
              )}
              <button className={tab === "note" ? "on" : ""} onClick={() => setTab("note")}>
                {t("panel.tabNote")}
              </button>
            </div>

            {tab === "trigger" && (
              <div className="side-body">
                <h3>{t("panel.triggerHeading")}</h3>
                <p className="muted" style={{ margin: "0 0 10px" }}>
                  {t("panel.triggerIntro")}
                </p>

                {armedTrigger ? (
                  <div className="armed" style={{ marginBottom: 12 }}>
                    <div className="row" style={{ justifyContent: "space-between" }}>
                      <strong>{armedTrigger}</strong>
                      <button
                        className="danger"
                        disabled={armTrigger.isPending}
                        onClick={() => armTrigger.mutate(null)}
                      >
                        {t("panel.disarm")}
                      </button>
                    </div>
                    <div className="muted mono" style={{ fontSize: 12, marginTop: 4 }}>
                      {armedConfig ? conditionSummary(armedConfig) : t("panel.conditionUnavailable")}
                      {armedConfig
                        ? t("panel.durationWindow", { n: armedConfig.duration_s ?? "?" })
                        : ""}
                    </div>
                  </div>
                ) : (
                  <p className="muted" style={{ marginTop: 0 }}>
                    {t("panel.noTriggerArmed")}
                  </p>
                )}

                <div className="row">
                  <select
                    value={triggerChoice ?? ""}
                    onChange={(e) => setTriggerChoice(e.target.value || null)}
                  >
                    <option value="">{t("panel.chooseTrigger")}</option>
                    {(triggers.data ?? []).map((trigger) => (
                      <option key={trigger.id} value={trigger.id}>
                        {trigger.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="primary"
                    disabled={!triggerChoice || armTrigger.isPending}
                    onClick={() => armTrigger.mutate(triggerChoice)}
                  >
                    {t("panel.arm")}
                  </button>
                </div>
                {(triggers.data ?? []).length === 0 && (
                  <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                    {t("panel.noTriggersYet")}
                  </p>
                )}
                {triggers.error && <ErrorBox error={triggers.error} />}

                <h3 style={{ marginBottom: 4 }}>{t("panel.latestCapture")}</h3>
                {captures.isLoading ? (
                  <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                    {t("panel.loadingShort")}
                  </p>
                ) : latestVideo && videoUrl.data ? (
                  <>
                    <video
                      key={latestVideo.id}
                      src={videoUrl.data.url}
                      controls
                      style={{ width: "100%", borderRadius: 8, background: "#000" }}
                    />
                    <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
                      {latestVideo.segment} ·{" "}
                      {t("panel.framesToMp4", {
                        n: (captures.data ?? []).filter(
                          (c) => c.kind === "frame" && c.segment === latestVideo.segment,
                        ).length,
                      })}
                    </p>
                  </>
                ) : (
                  <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                    {t("panel.noVideoYet")}
                  </p>
                )}
                {captures.error && <ErrorBox error={captures.error} />}
              </div>
            )}

            {tab === "apps" && (
              <div className="side-body">
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <h3>{t("panel.appsHeading")}</h3>
                  <button
                    disabled={!connected || appsQuery.isFetching}
                    onClick={() => appsQuery.refetch()}
                    title={t("panel.refresh")}
                  >
                    {appsQuery.isFetching ? t("panel.loading") : t("panel.refresh")}
                  </button>
                </div>
                <p className="muted" style={{ margin: 0 }}>
                  {t("panel.appsIntro")}
                </p>
                <input
                  placeholder={t("panel.filterApps")}
                  value={appFilter}
                  onChange={(e) => setAppFilter(e.target.value)}
                />
                {!connected && (
                  <p className="muted" style={{ margin: 0 }}>
                    {t("panel.connectToLoad")}
                  </p>
                )}
                {appsQuery.error && <ErrorBox error={appsQuery.error} />}
                {connected && appsQuery.isLoading && (
                  <p className="muted" style={{ margin: 0 }}>
                    {t("panel.loadingShort")}
                  </p>
                )}
                {connected && !appsQuery.isLoading && visibleApps.length === 0 && !appsQuery.error && (
                  <p className="muted" style={{ margin: 0 }}>
                    {appFilter.trim() ? t("panel.noAppsFilter") : t("panel.noApps")}
                  </p>
                )}
                <ul className="app-list">
                  {visibleApps.map((app) => (
                    <li key={app.package}>
                      <button
                        disabled={launching !== null}
                        onClick={() => launchApp(app.package)}
                        title={app.package}
                      >
                        {app.icon_b64 ? (
                          <img
                            className="app-icon"
                            alt=""
                            draggable={false}
                            decoding="async"
                            src={`data:${app.icon_mime ?? "image/png"};base64,${app.icon_b64}`}
                          />
                        ) : (
                          <span className="app-icon placeholder" aria-hidden>
                            {(app.label.trim()[0] ?? "?").toUpperCase()}
                          </span>
                        )}
                        <span className="app-meta">
                          <span>{app.label}</span>
                          <span className="pkg mono">{app.package}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Not gated on `hasModules`: a phone that stops reporting one
                while the tab is open should say so, not go blank. */}
            {tab === "modules" && (
              <DeviceModules deviceId={id} beats={device.data!.modules ?? {}} />
            )}

            {tab === "note" && (
              <div className="side-body">
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <h3>{t("panel.noteHeading")}</h3>
                  {noteDraft === null ? (
                    <button
                      className="icon-btn"
                      onClick={() => setNoteDraft(device.data!.note ?? "")}
                      title={t("panel.editNote")}
                    >
                      <Icon name="pencil" />
                    </button>
                  ) : (
                    <button
                      className="primary"
                      disabled={saveNote.isPending}
                      onClick={() => saveNote.mutate()}
                    >
                      {t("panel.save")}
                    </button>
                  )}
                </div>
                {noteDraft === null ? (
                  <p className="muted" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                    {device.data!.note || t("panel.noNote")}
                  </p>
                ) : (
                  <textarea
                    value={noteDraft}
                    rows={8}
                    autoFocus
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && setNoteDraft(null)}
                    placeholder={t("panel.notePlaceholder")}
                  />
                )}
                {saveNote.error && <ErrorBox error={saveNote.error} />}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
